import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { Captcha } from "@/components/Captcha.jsx";
import { Link } from "react-router-dom";
import { ChevronRight } from 'lucide-react';
import { generateHolyPseudo } from "@/utils/holyPseudoGenerator.js";
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";

export const DemoStepForm = ({
    userInfo,
    setUserInfo,
    userScore,
    isLoading,
    apiError,
    handleFormSubmit,
    onBack,
    isDemo = false
}) => {
    const { t } = useTranslation();
    const { brand } = useBrandConfig();
    const formRef = useRef(null);
    const captchaRef = useRef(null);

    const [captchaToken, setCaptchaToken] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);

    const skipCaptcha = isDemo || import.meta.env.DEV;

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleFocus = (e) => {
        const target = e.target;
        setTimeout(() => { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 300);
    };

    const displayError = React.useMemo(() => {
        if (!apiError) return null;
        if (typeof apiError === 'object') {
            if (apiError.data && apiError.data.email) return t('form.error_email_invalid');
        }
        return t('form.generic_error');
    }, [apiError, t]);

    useEffect(() => {
        if (!apiError) return;
        const errorString = JSON.stringify(apiError);
        const errorCode = apiError.error || "";
        if (
            errorString.includes("ALREADY_PLAYED") ||
            errorString.includes("ParticipantAlreadyPlayedException") ||
            errorCode === "conflict"
        ) {
            setIsAlreadyRegistered(true);
        } else {
            setCaptchaToken(null);
            captchaRef.current?.reset();
        }
    }, [apiError]);

    useEffect(() => {
        if (!userInfo.pseudo) {
            setUserInfo({ ...userInfo, pseudo: generateHolyPseudo() });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAutoPseudo = (e) => {
        e.preventDefault();
        setIsAnimating(true);
        setUserInfo({ ...userInfo, pseudo: generateHolyPseudo() });
        setTimeout(() => setIsAnimating(false), 300);
    };

    const onFormSubmit = (e) => {
        e.preventDefault();
        if (!skipCaptcha && !captchaToken) return;
        handleFormSubmit(e, { captchaToken: skipCaptcha ? 'bypass-token' : captchaToken });
    };

    // VUE 1 : DÉJÀ INSCRIT
    if (isAlreadyRegistered) {
        return (
            <div className="mt-4 animate-fade-in pb-8 font-body max-w-sm mx-auto w-full relative z-30 px-4">
                <div className="relative bg-[#EBE1CB]/60 backdrop-blur-3xl rounded-[2.5rem] p-8 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.15)] border border-white/40 text-center flex flex-col items-center">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6 shadow-lg border-2 border-white/50 animate-bounce" style={{ backgroundColor: brand.colors.highlight }}>
                        <span className="text-4xl">😅</span>
                    </div>
                    <h3 className="text-[3rem] font-heading text-black uppercase italic leading-none tracking-tighter mb-4">
                        {t('form.already_registered.title')}
                    </h3>
                    <p className="text-sm font-bold text-black/60 mb-6 leading-relaxed">
                        {t('form.already_registered.description')} <br/>
                        <span className="text-black bg-white/50 px-2 py-1 rounded-md inline-block mt-2 tracking-widest">{userInfo.email}</span>
                    </p>
                    <div className="border p-4 rounded-2xl mb-6 w-full" style={{ backgroundColor: `${brand.colors.accent}33`, borderColor: `${brand.colors.accent}66` }}>
                        <p className="text-xs text-black font-black uppercase tracking-widest">
                            {t('form.already_registered.email_sent')}
                        </p>
                    </div>
                    <p className="text-[10px] font-bold text-black/40 uppercase tracking-widest mb-8">
                        {t('form.already_registered.spam_hint')}
                    </p>
                    <button
                        onClick={onBack}
                        className="w-full py-4 bg-black text-white rounded-full font-heading italic text-xl uppercase tracking-wider shadow-[0_12px_30px_rgba(0,0,0,0.3)] active:translate-y-1 active:shadow-none transition-all"
                    >
                        {t('form.already_registered.back_btn')}
                    </button>
                </div>
            </div>
        );
    }

    // VUE 2 : FORMULAIRE PRINCIPAL
    const isSubmitDisabled = isLoading || (!skipCaptcha && !captchaToken);

    return (
        <div ref={formRef} className="mt-4 animate-fade-in pb-8 font-body max-w-sm mx-auto w-full relative z-30 px-4">

            {/* HEADER */}
            <div className="text-center mb-6">
                <div className="text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-[0.2em] mb-3 inline-block italic shadow-sm" style={{ backgroundColor: brand.colors.highlight, color: brand.colors.highlightText }}>
                    {t('holy.form.step_badge')}
                </div>
                <h3 className="text-[3.5rem] font-heading text-black uppercase italic leading-[0.85] tracking-tighter drop-shadow-sm mb-2">
                    {t('holy.form.title_line1')} <br/>
                    <span style={{ color: brand.colors.error }}>{t('holy.form.title_line2')}</span>
                </h3>
                <p className="text-[11px] font-bold text-black/50 uppercase tracking-[0.15em] mt-3">
                    {t('holy.form.score_label')} <span className="bg-white/40 px-2 py-0.5 rounded text-black font-black">{userScore.team1} - {userScore.team2}</span>
                </p>
            </div>

            {/* CARTE FORMULAIRE */}
            <div className="bg-white rounded-[2rem] p-6 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] flex flex-col mb-6 relative z-20">
                <form id="demo-form" onSubmit={onFormSubmit} className="space-y-4">

                    {/* INPUT PSEUDO */}
                    <div className="relative group">
                        <input
                            type="text"
                            placeholder={t('form.placeholder_pseudo')}
                            value={userInfo.pseudo}
                            onChange={(e) => setUserInfo({ ...userInfo, pseudo: e.target.value })}
                            onFocus={handleFocus}
                            required
                            minLength={3}
                            disabled={isLoading}
                            className={`w-full px-5 py-4 bg-[#F4F4F4] border-2 border-transparent rounded-2xl focus:outline-none focus:bg-white focus:border-black text-black font-black placeholder:text-black/30 transition-all ${isAnimating ? 'scale-[0.98] bg-gray-100' : ''}`}
                        />
                        <button
                            type="button"
                            onClick={handleAutoPseudo}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black text-[10px] font-black uppercase tracking-widest px-4 py-2.5 rounded-xl shadow-sm hover:bg-gray-800 active:scale-95 transition-all"
                            style={{ color: brand.colors.highlight }}
                        >
                            {t('holy.form.auto_btn')}
                        </button>
                    </div>

                    {/* INPUT EMAIL */}
                    <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        placeholder={t('form.placeholder_email')}
                        value={userInfo.email}
                        onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                        onFocus={handleFocus}
                        required
                        disabled={isLoading}
                        className="w-full px-5 py-4 bg-[#F4F4F4] border-2 border-transparent rounded-2xl focus:outline-none focus:bg-white focus:border-black text-black font-black placeholder:text-black/30 transition-all"
                    />

                    {/* CHECKBOX CGU */}
                    {!isDemo && (
                        <label className="flex items-start gap-3 pt-2 pb-1 cursor-pointer group">
                            <div
                                className={`relative flex items-center justify-center w-6 h-6 rounded-lg border-2 transition-all shrink-0 mt-0.5 ${userInfo.acceptTerms ? 'border-transparent' : 'bg-[#F4F4F4] border-gray-200 group-hover:border-black'}`}
                                style={userInfo.acceptTerms ? { backgroundColor: brand.colors.accent, borderColor: brand.colors.accent } : {}}
                            >
                                <input
                                    type="checkbox"
                                    required
                                    checked={userInfo.acceptTerms || false}
                                    onChange={(e) => setUserInfo({ ...userInfo, acceptTerms: e.target.checked })}
                                    disabled={isLoading}
                                    className="absolute opacity-0 cursor-pointer w-full h-full"
                                />
                                {userInfo.acceptTerms && (
                                    <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                            <span className="text-[11px] font-bold text-black/50 leading-snug">
                                {t('holy.form.accept_terms_1')} <Link to="/cgu" target="_blank" rel="noopener noreferrer" className="text-black underline">{t('holy.form.accept_terms_link1')}</Link> {t('holy.form.accept_terms_2')} <Link to="/rules" target="_blank" rel="noopener noreferrer" className="text-black underline">{t('holy.form.accept_terms_link2')}</Link>.
                            </span>
                        </label>
                    )}

                    {/* ERREUR */}
                    {displayError && !isAlreadyRegistered && (
                        <div className="text-black text-xs font-black uppercase tracking-widest p-4 rounded-xl text-center" style={{ backgroundColor: brand.colors.error }}>
                            {displayError}
                        </div>
                    )}

                    {/* CAPTCHA */}
                    {!skipCaptcha && (
                        <div className="rounded-2xl overflow-hidden border-2 border-gray-100">
                            <Captcha ref={captchaRef} onVerify={setCaptchaToken} />
                        </div>
                    )}
                </form>
            </div>

            {/* BOUTON DE VALIDATION */}
            <button
                id="demo-btn-submit"
                type="submit"
                form="demo-form"
                disabled={isSubmitDisabled}
                className="group w-full py-5 rounded-full font-heading italic text-2xl uppercase tracking-wider transition-all flex items-center justify-center gap-3"
                style={isSubmitDisabled
                    ? { backgroundColor: 'rgba(0,0,0,0.1)', color: 'rgba(0,0,0,0.3)', cursor: 'not-allowed' }
                    : { backgroundColor: brand.colors.accent, color: brand.colors.accentText, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' }
                }
            >
                {isLoading ? (
                    <span className="animate-pulse">{t('form.loading_btn')}</span>
                ) : (
                    <>
                        {t('form.submit_btn')}
                        <ChevronRight size={28} strokeWidth={3} className="group-hover:translate-x-1 transition-transform" />
                    </>
                )}
            </button>

            {/* MODIFIER LE SCORE */}
            <button
                onClick={() => !isLoading && onBack()}
                disabled={isLoading}
                className="w-full mt-6 py-2 text-[10px] font-black uppercase tracking-widest text-black/40 hover:text-black disabled:opacity-50 transition-colors"
            >
                {t('form.modify_btn')}
            </button>
        </div>
    );
};
